import { useMemo } from "react";
import type { Event, EventMarket } from "hooks/firestore/betting/useBetting";
import type { Maybe } from "types/utilities";

const formulaOneMarketOrder: Record<string, number> = {
  Winner: 1,
  "Top 3": 2,
  "Top 6": 3,
  "Top 10 (Points Finish)": 4,
  "Fastest Lap - Winner": 5,
  "Fastest Lap": 6,
  Winning: 7,
  "Grid Position of Winner": 8,
  "Virtual Safety Car": 9,
  "Safety Car Period": 10,
  "Number of classified drivers": 11,
  "First Driver to make a Pit Stop": 12,
  "First Driver Retirement": 13,
  "First Constructor Retirement": 14,
  "Points finish": 15,
  Classified: 16,
  default: Number.MAX_VALUE,
};

export const sortMarkets = (
  marketA: EventMarket,
  marketB: EventMarket,
): number => {
  return marketA.name > marketB.name ? 1 : -1;
};

const sortFormulaOneMarkets = (
  marketA: EventMarket,
  marketB: EventMarket,
): number => {
  // market names usually formatted as
  // "Name of Race - Name of Market" but in some cases it'll be -> "Name of Race - Name of Market - Extra info"
  // we ignore the race name
  const [marketAName] = marketA.name.split("-").slice(1);
  const [marketBName] = marketB.name.split("-").slice(1);

  // clean up the market names so we can get a match in the market order object
  const formattedMarketAName = marketAName
    ? marketAName.trim().toLowerCase()
    : undefined;
  const formattedMarketBName = marketBName
    ? marketBName.trim().toLowerCase()
    : undefined;

  const marketAOrderKey = Object.keys(formulaOneMarketOrder).find(
    (marketOrderKey) => {
      if (formattedMarketAName === "fastest lap") {
        return marketOrderKey === "Fastest Lap - Winner";
      }
      return formattedMarketAName?.includes(marketOrderKey.toLowerCase());
    },
  );
  const marketBOrderKey = Object.keys(formulaOneMarketOrder).find(
    (marketOrderKey) => {
      if (formattedMarketBName === "fastest lap") {
        return marketOrderKey === "Fastest Lap - Winner";
      }
      return formattedMarketBName?.includes(marketOrderKey.toLowerCase());
    },
  );

  return (
    formulaOneMarketOrder[marketAOrderKey || "default"] -
    formulaOneMarketOrder[marketBOrderKey || "default"]
  );
};

export const useOutrightMarkets = (outright: Maybe<Event>): EventMarket[] => {
  return useMemo(() => {
    if (!outright) {
      return [];
    }

    const filteredMarkets =
      outright.markets?.filter((market) => {
        // only show active markets
        if (market.status !== "ACTIVE") {
          return false;
        }

        if (!Object.values(market.outcomes).some((outcome) => outcome.active)) {
          // hide markets with no active outcomes
          return false;
        }

        return true;
      }) ?? [];

    if (outright.sport === "FORMULA_ONE") {
      return filteredMarkets.sort(sortFormulaOneMarkets);
    } else {
      return filteredMarkets.sort(sortMarkets);
    }
  }, [outright]);
};
